import { useCallback, useMemo } from 'react';
import type { MouseEvent } from 'react';

import { AvatarGroup, Box, Stack, Typography } from '@mui/material';
import { getCurrencyByCountryCode } from '@realadvisor/constants';
import { formatDistance } from 'date-fns';
import { House, Minus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import type { GetDashboardNewLeadsQuery } from '@graphql/__generated__/graphql';
import { ClaimLeadButton } from '@modules/_shared/components/ClaimLeadButton';
import { PassLeadButton } from '@modules/_shared/components/PassLeadButton';
import { UserAvatar } from '@modules/_shared/components/UserAvatar';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { getAppraisalPerception } from '@modules/_shared/utils/appraisal';
import { getContactDisplayNames } from '@modules/_shared/utils/contact';
import {
  formatAddress,
  formatAppraisalPrice,
  formatIncome,
  getLeadLocation,
  hasValidAppraisalData,
} from '@modules/_shared/utils/formatting';
import {
  translateAppraisalPerception,
  translateAppraisalReason,
  translateBuyHorizon,
  translateMortgageBuyingStatus,
  translateMortgageStatus,
  translateOpenToOffer,
  translateReceivedMortgageOffer,
  translateSaleHorizon,
  translateSellingWith,
} from '@modules/_shared/utils/lead-labels';
import { userCanViewLeadDetails } from '@modules/_shared/utils/listingPermissions';
import {
  makeRowKeyDownHandler,
  navigateWithModifiers,
} from '@modules/_shared/utils/navigation';
import {
  COMMERCIAL_TYPES,
  LAND_TYPES,
  MULTI_FAMILY_TYPES,
} from '@modules/_shared/utils/propertyTypes';
import { useLocale } from '@shared/hooks/locale';
import { formatPrice } from '@shared/utils/format-price';
import { getStaticMapLink } from '@shared/utils/static-map';

type DashboardLeadsCardItemProps = {
  lead: GetDashboardNewLeadsQuery['leads'][number];
  showAssignedAgents?: boolean;
  onRemove: (leadId: string) => void;
};

export const DashboardLeadsCardItem = ({
  lead,
  showAssignedAgents = false,
  onRemove,
}: DashboardLeadsCardItemProps) => {
  const { dateLocale, locale, t } = useLocale();
  const navigate = useNavigate();
  const { me } = useAppData();

  const assignedAgents = useMemo(() => {
    if (!showAssignedAgents) {
      return [];
    }
    const agents = lead.lead_agents ?? [];
    if (agents.length === 0) {
      return [];
    }

    return [...agents].sort((a, b) => {
      const aTime = new Date(a.created_at).getTime();
      const bTime = new Date(b.created_at).getTime();
      return bTime - aTime;
    });
  }, [lead.lead_agents, showAssignedAgents]);

  const canViewLeadDetails = useMemo(
    () => userCanViewLeadDetails(me, lead),
    [me, lead],
  );
  const restrictedAgentView =
    !(me?.is_admin ?? false) &&
    (lead.lead_agents ?? []).some(
      agent => agent.user_id === me?.id && agent.claimed_at == null,
    );
  const showFullMap = canViewLeadDetails && !restrictedAgentView;
  const currency = getCurrencyByCountryCode(
    lead.property?.country_code ?? lead.appraisal_property_country_code ?? 'CH',
  );

  const leadPath = useMemo(() => `/dashboard/leads/${lead.id}`, [lead.id]);

  const handleCardClick = (event: MouseEvent<HTMLElement>) => {
    navigateWithModifiers(event, navigate, leadPath);
  };

  const handleClaimClick = useCallback(() => {
    onRemove(lead.id);
    navigate(leadPath);
  }, [lead.id, navigate, leadPath, onRemove]);

  const { displayName } = getContactDisplayNames(lead, canViewLeadDetails);

  const mapUrl = useMemo(() => {
    const mapLat = lead.property?.lat ?? lead.appraisal_property_lat ?? null;
    const mapLng = lead.property?.lng ?? lead.appraisal_property_lng ?? null;
    if (mapLat == null || mapLng == null) {
      return null;
    }
    return getStaticMapLink({
      lat: mapLat,
      lng: mapLng,
      zoom: showFullMap ? 19 : 14,
      style: 'satellite',
      width: 200,
      height: 200,
      markers: showFullMap ? [{ lat: mapLat, lng: mapLng }] : [],
    });
  }, [
    showFullMap,
    lead.property?.lat,
    lead.property?.lng,
    lead.appraisal_property_lat,
    lead.appraisal_property_lng,
  ]);

  const timeAgo = useMemo(() => {
    // Latest assignment on the lead — the same value the list is sorted by,
    // not the tab-filtered subset, so the order always reads as sorted.
    const assignedAt =
      lead.lead_agents_aggregate?.aggregate?.max?.created_at ??
      lead.lead_agents?.[0]?.created_at;
    return formatDistance(
      new Date(assignedAt ?? lead.requalified_or_created_at),
      new Date(),
      {
        locale: dateLocale,
        addSuffix: true,
      },
    );
  }, [
    lead.lead_agents_aggregate,
    lead.lead_agents,
    lead.requalified_or_created_at,
    dateLocale,
  ]);

  const appraisalIndicator = useMemo(
    () =>
      getAppraisalPerception(
        lead.appraisal_perception,
        lead.appraisal_perceived_value,
        lead.property?.latest_appraisal?.max,
        lead.property?.latest_appraisal?.min,
      ),
    [
      lead.appraisal_perceived_value,
      lead.appraisal_perception,
      lead.property?.latest_appraisal?.max,
      lead.property?.latest_appraisal?.min,
    ],
  );

  const priceRange = useMemo(
    () =>
      formatAppraisalPrice(lead.property?.latest_appraisal, currency, locale),
    [lead.property?.latest_appraisal, currency, locale],
  );

  const isCommercial = (name?: string | null) =>
    (COMMERCIAL_TYPES as readonly string[]).includes(name ?? '');

  const isMultiDwelling = (name?: string | null) =>
    (MULTI_FAMILY_TYPES as readonly string[]).includes(name ?? '');

  const isLand = (name?: string | null) =>
    (LAND_TYPES as readonly string[]).includes(name ?? '');

  const formatSurface = useCallback(
    (n?: number | null): string | null => {
      if (n == null) {
        return null;
      }
      return `${n.toLocaleString(locale, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      })} m²`;
    },
    [locale],
  );

  const totalRentalIncome = useMemo(() => {
    let income = 0;
    if (
      lead.property?.property_type?.name === 'house_multiple_dwelling' &&
      lead.property?.residential_yearly_rental_income != null
    ) {
      income += lead.property.residential_yearly_rental_income;
    }
    if (
      lead.property?.property_type?.name === 'indus_commercial' &&
      lead.property?.commercial_yearly_rental_income != null
    ) {
      income += lead.property.commercial_yearly_rental_income;
    }
    if (
      lead.property?.property_type?.name ===
        'indus_commercial_and_residential' &&
      lead.property?.commercial_yearly_rental_income != null &&
      lead.property?.residential_yearly_rental_income != null
    ) {
      income +=
        lead.property.commercial_yearly_rental_income +
        lead.property.residential_yearly_rental_income;
    }
    if (
      lead.property?.income_includes_parking === false &&
      lead.property?.parking_yearly_rental_income != null
    ) {
      income += lead.property.parking_yearly_rental_income;
    }
    return income;
  }, [
    lead.property?.property_type?.name,
    lead.property?.residential_yearly_rental_income,
    lead.property?.commercial_yearly_rental_income,
    lead.property?.income_includes_parking,
    lead.property?.parking_yearly_rental_income,
  ]);

  const getPropertyDetails = useCallback(() => {
    const property = lead.property;
    if (!property) {
      return 'Property details TBD';
    }

    const details: Array<string | null> = [];

    details.push(property.property_type?.label ?? null);

    if (isMultiDwelling(property.property_type?.name)) {
      details.push(
        t('numberOfResidentialUnitsCount', {
          count: property.number_of_residential_units ?? 0,
        }),
      );
      details.push(
        t('surfaceOf', {
          surface: (property.residential_surface ?? 0).toLocaleString(locale, {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
          }),
        }),
      );
    }

    // Commercial details
    if (isCommercial(property.property_type?.name)) {
      details.push(
        t('numberOfCommercialTenantsCount', {
          count: property.number_of_commercial_units ?? 0,
        }),
      );
      details.push(
        t('surfaceOf', {
          surface: (property.commercial_surface ?? 0).toLocaleString(locale, {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
          }),
        }),
      );
    }

    // Rooms/Bedrooms for non-commercial and not land
    if (
      !isCommercial(property.property_type?.name) &&
      !isLand(property.property_type?.name)
    ) {
      if (property.number_of_rooms != null) {
        details.push(t('roomsShort', { count: property.number_of_rooms }));
      } else if (property.number_of_bedrooms != null) {
        details.push(
          t('numberOfBedroomsWithCount', {
            count: property.number_of_bedrooms,
          }),
        );
      }
    }

    // Income or surfaces
    if (isCommercial(property.property_type?.name)) {
      details.push(
        t('totalRentalIncome', {
          income: formatIncome(totalRentalIncome, currency, locale),
        }),
      );
    } else if (!isLand(property.property_type?.name)) {
      details.push(
        property.living_surface != null
          ? formatSurface(property.living_surface)
          : property.built_surface != null
          ? formatSurface(property.built_surface)
          : null,
      );
    }

    // Plot size vs land surface
    if (isLand(property.property_type?.name)) {
      if (property.land_surface != null) {
        details.push(
          t('surfaceOf', {
            surface: property.land_surface.toLocaleString(locale, {
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
            }),
          }),
        );
      }
    } else if (property.land_surface != null) {
      details.push(
        t('plotSurfaceOf', {
          plotSurface: property.land_surface.toLocaleString(locale, {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
          }),
        }),
      );
    }

    if (isLand(property.property_type?.name) && property.plot_number != null) {
      details.push(`${t('Plot')} ${property.plot_number ?? ''}`);
    }

    return details.filter(Boolean).join(' • ');
  }, [formatSurface, lead.property, locale, currency, t, totalRentalIncome]);

  const propertyDetails = useMemo(
    () => getPropertyDetails(),
    [getPropertyDetails],
  );

  const getQualificationFunnel = useCallback(() => {
    const getStep1 = () => {
      if (
        (isCommercial(lead.property?.property_type?.name) ||
          isMultiDwelling(lead.property?.property_type?.name)) &&
        lead.ownership
      ) {
        switch (lead.ownership) {
          case 'sole_ownership':
            return t('Sole ownership');
          case 'co_ownership':
            return t('Multiple private owners');
          case 'corporate_ownership':
            return t('Owned by a company');
          default:
            return null;
        }
      } else {
        switch (lead.relationship) {
          case 'owner':
            return t('Owner');
          case 'past_owner':
            return t('Past owner');
          case 'tenant':
            return t('Tenant renter');
          case 'agent':
            return t('Agent');
          case 'other':
            return t('Other');
          default:
            return null;
        }
      }
    };

    const getStep3 = () => {
      switch (lead.appraisal_reason) {
        case 'sell':
          return translateSaleHorizon(t, lead.sale_horizon || 'not_set');
        case 'refinance':
          return null;
        case 'separation':
          return lead.appraisal_separation_next_step || null;
        case 'inheritance':
          return lead.appraisal_inheritance_next_step || null;
        case 'buy_soon':
          return translateBuyHorizon(t, lead.buy_horizon || 'not_set');
        default:
          return null;
      }
    };

    const getSellingWith = () => {
      return translateSellingWith(t, lead.appraisal_selling_with || '');
    };

    const getStep4 = () => {
      if (
        (lead.appraisal_reason === 'other_not_owner' &&
          lead.relationship === 'tenant') ||
        lead.appraisal_reason === 'move_rental'
      ) {
        return lead.appraisal_monthly_gross_rent
          ? formatPrice(lead.appraisal_monthly_gross_rent, locale, currency)
          : null;
      }
      return lead.appraisal_perceived_value != null
        ? formatPrice(lead.appraisal_perceived_value, locale, currency)
        : null;
    };

    const getOpenToOffer = () => {
      return translateOpenToOffer(t, lead.appraisal_open_to_offer || '');
    };

    const getMarketTime = () => {
      if (lead.appraisal_market_time != null) {
        return t('sinceMonths', {
          numberMonths: lead.appraisal_market_time,
        });
      }
      return null;
    };

    if (getStep1() == null) {
      return [
        translateAppraisalReason(t, lead.appraisal_reason || 'not_set'),
        translateMortgageBuyingStatus(t, lead.mortgage_buying_status),
        translateMortgageStatus(t, lead.mortgage_status),
        translateReceivedMortgageOffer(t, lead.received_mortgage_offer ?? null),
      ]
        .filter(Boolean)
        .join(' • ');
    }

    return [
      // step 1
      getStep1(),
      // step 2
      lead.appraisal_reason !== 'not_set'
        ? translateAppraisalReason(t, lead.appraisal_reason || 'not_set')
        : null,
      // step 3
      getStep3(),
      getSellingWith(),
      // step 4
      getStep4(),
      // step 5
      lead.appraisal_perception == null
        ? null
        : translateAppraisalPerception(t, lead.appraisal_perception),
      // step 6
      lead.appraisal_next_step,
      getOpenToOffer(),
      getMarketTime(),
    ]
      .filter(Boolean)
      .join(' • ');
  }, [
    lead.appraisal_reason,
    lead.appraisal_perception,
    lead.appraisal_perceived_value,
    lead.appraisal_selling_with,
    lead.appraisal_market_time,
    lead.appraisal_next_step,
    lead.appraisal_monthly_gross_rent,
    lead.appraisal_separation_next_step,
    lead.appraisal_inheritance_next_step,
    lead.appraisal_open_to_offer,
    lead.buy_horizon,
    lead.sale_horizon,
    lead.mortgage_buying_status,
    lead.mortgage_status,
    lead.received_mortgage_offer,
    lead.ownership,
    lead.relationship,
    lead.property?.property_type?.name,
    t,
    locale,
    currency,
  ]);

  const qualificationFunnel = useMemo(
    () => getQualificationFunnel(),
    [getQualificationFunnel],
  );

  const addressDisplay = useMemo(() => {
    const property = lead.property;
    // Always drop state + country — too long for a card and adds no value.
    if (property && canViewLeadDetails) {
      return formatAddress(property, ', ') || property.locality || null;
    }

    const parts = [];
    const { postcode, locality } = getLeadLocation(lead);
    if (postcode) {
      parts.push(postcode);
    }
    if (locality) {
      parts.push(locality);
    }
    return parts.length > 0 ? parts.join(' ') : null;
  }, [canViewLeadDetails, lead]);

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={makeRowKeyDownHandler(leadPath, navigate)}
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: 'stretch',
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 2,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
        '&:hover': {
          bgcolor: 'action.hover',
          borderColor: 'grey.300',
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: { xs: '100%', sm: 128 },
          height: { xs: 120, sm: 'auto' },
          alignSelf: 'stretch',
          flexShrink: 0,
          overflow: 'hidden',
        }}
      >
        {mapUrl != null ? (
          <Box
            component="img"
            src={mapUrl}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              display: 'block',
              objectFit: 'cover',
            }}
          />
        ) : (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'grey.100',
              color: 'grey.500',
            }}
          >
            <House size={28} strokeWidth={1.75} />
          </Box>
        )}
      </Box>
      <Stack
        sx={{
          flex: 1,
          minWidth: 0,
          p: 1.5,
          gap: 0.25,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography
            variant="body2"
            noWrap
            sx={{
              fontWeight: 600,
              minWidth: 0,
            }}
          >
            {addressDisplay ?? displayName}
          </Typography>
          <Stack
            direction="row"
            spacing={0.75}
            sx={{
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            {assignedAgents.length > 0 && (
              <AvatarGroup
                max={3}
                sx={{ '& .MuiAvatar-root': { width: 18, height: 18 } }}
              >
                {assignedAgents.map(agent => (
                  <UserAvatar
                    key={agent.id}
                    user={agent.user ?? undefined}
                    size={18}
                  />
                ))}
              </AvatarGroup>
            )}
            <Typography
              variant="caption"
              noWrap
              sx={{
                color: 'text.secondary',
              }}
            >
              {timeAgo}
            </Typography>
          </Stack>
        </Box>

        {propertyDetails && (
          <Typography
            variant="caption"
            noWrap
            sx={{
              color: 'text.secondary',
            }}
          >
            {propertyDetails}
          </Typography>
        )}

        {qualificationFunnel && (
          <Typography
            variant="caption"
            noWrap
            sx={{
              color: 'text.secondary',
            }}
          >
            {qualificationFunnel}
          </Typography>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: { xs: 0.75, sm: 1 },
            mt: 0.25,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              minWidth: 0,
            }}
          >
            {appraisalIndicator}
            {hasValidAppraisalData(lead.property?.latest_appraisal) ? (
              <Typography
                variant="body2"
                noWrap
                sx={{
                  fontWeight: 700,
                }}
              >
                {priceRange}
              </Typography>
            ) : (
              <Box
                sx={{
                  color: 'grey.400',
                  display: 'inline-flex',
                  flexShrink: 0,
                }}
              >
                <Minus aria-hidden size={16} />
              </Box>
            )}
          </Box>
          <Stack
            direction="row"
            spacing={0.75}
            sx={{
              flexShrink: 0,
              width: { xs: '100%', sm: 'auto' },
              '& > *': {
                flex: { xs: 1, sm: 'initial' },
              },
            }}
            onClick={e => e.stopPropagation()}
          >
            <PassLeadButton
              leadId={lead.id}
              passForUserId={assignedAgents[0]?.user_id ?? me?.id}
              size="small"
              onPass={() => onRemove(lead.id)}
            />
            <ClaimLeadButton
              leadId={lead.id}
              claimForUserId={assignedAgents[0]?.user_id ?? me?.id}
              color="success"
              size="small"
              onSuccess={handleClaimClick}
            />
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};
